// Lightweight HTTP utilities to mature our fetch usage without changing behavior
// Step 1: timeout + request id helpers (no retries by default to avoid breaking flows)

export function generateRequestId(): string {
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = 15000,
): Promise<Response> {
  if (timeoutMs <= 0) {
    // No timeout
    return fetch(input, init);
  }

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(input, { ...init, signal: controller.signal });
    return resp;
  } finally {
    clearTimeout(id);
  }
}

export async function parseJsonSafe<T = unknown>(resp: Response): Promise<T | undefined> {
  const contentType = resp.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return undefined;
  try {
    return (await resp.json()) as T;
  } catch {
    return undefined;
  }
}

export type RequestInterceptor = (ctx: {
  url: string;
  init: RequestInit;
}) => Promise<{ url: string; init: RequestInit }> | { url: string; init: RequestInit };

export type ResponseInterceptor = (ctx: {
  url: string;
  init: RequestInit;
  response: Response;
}) => Promise<Response> | Response;

export class HttpClient {
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  constructor(
    private readonly timeoutMs = 15000,
    private readonly maxRetries = 2,
  ) {}

  useRequest(interceptor: RequestInterceptor) {
    this.requestInterceptors.push(interceptor);
  }

  useResponse(interceptor: ResponseInterceptor) {
    this.responseInterceptors.push(interceptor);
  }

  async request(url: string, init: RequestInit = {}): Promise<Response> {
    // Apply request interceptors sequentially
    let ctx = { url, init };
    for (const interceptor of this.requestInterceptors) {
      ctx = await interceptor(ctx);
    }

    // Ensure we have a request id
    const headers = new Headers(ctx.init.headers as HeadersInit);
    if (!headers.has("X-Request-Id")) {
      headers.set("X-Request-Id", generateRequestId());
    }
    ctx.init.headers = headers;

    // Basic retry for idempotent methods on transient errors
    const method = (ctx.init.method || "GET").toString().toUpperCase();
    const override = (ctx.init as unknown as { __retry?: { maxRetries?: number; idempotent?: boolean } }).__retry;
    const localMaxRetries = typeof override?.maxRetries === 'number' ? override!.maxRetries! : this.maxRetries;
    const idempotent = typeof override?.idempotent === 'boolean' ? override!.idempotent! : (method === "GET" || method === "HEAD");

    let attempt = 0;
    while (true) {
      let response = await fetchWithTimeout(ctx.url, ctx.init, this.timeoutMs);

      // Apply response interceptors sequentially (even on errors) for logging/metrics
      for (const interceptor of this.responseInterceptors) {
        response = await interceptor({ url: ctx.url, init: ctx.init, response });
      }

      if (!response.ok) {
        const retryAfterHeader = response.headers.get("retry-after");
        const retryAfterMs = parseRetryAfter(retryAfterHeader);
        const status = response.status;

        const retryable = idempotent && (status === 408 || status === 429 || status === 502 || status === 503 || status === 504);

        if (retryable && attempt < localMaxRetries) {
          attempt += 1;
          const backoff = retryAfterMs ?? backoffWithJitter(attempt);
          await delay(backoff);
          continue; // retry loop
        }

        // Build and throw HttpError with humanized message
        throw await buildHttpError(ctx.url, response);
      }
      return response;
    }
  }
}

export class HttpError extends Error {
  constructor(
    public status: number,
    public url: string,
    message: string,
    public requestId?: string | null,
    public details?: unknown,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

function humanize(status: number, serverMsg?: string): string {
  switch (status) {
    case 400:
      return `Invalid request${serverMsg ? `: ${serverMsg}` : ""}`;
    case 401:
      return "Unauthorized. Please check your session or API key.";
    case 403:
      return serverMsg || "Forbidden. You don't have access to this resource.";
    case 404:
      return "Not found. The resource may have been deleted or moved.";
    case 409:
      return `Conflict${serverMsg ? `: ${serverMsg}` : ""}`;
    case 413:
      return "Payload too large. File exceeds server or body size limit.";
    case 422:
      return `Unprocessable entity${serverMsg ? `: ${serverMsg}` : ""}`;
    case 429:
      return "Too many requests. Please slow down and try again.";
    default:
      if (status >= 500) return "Server error. Please try again later.";
      return serverMsg || "Request failed.";
  }
}

async function buildHttpError(url: string, response: Response): Promise<HttpError> {
  const ct = response.headers.get("content-type") || "";
  let serverMsg: string | undefined;
  let details: unknown = undefined;
  if (ct.includes("application/json")) {
    try {
      const j = (await response.json()) as unknown;
      details = j;
      if (j && typeof j === "object") {
        const r = j as Record<string, unknown>;
        serverMsg =
          (typeof r.message === "string" && r.message) ||
          (typeof r.error === "string" && r.error) ||
          undefined;
      }
    } catch {
      // fallthrough to text
    }
  }
  if (!serverMsg) {
    try {
      const t = await response.text();
      serverMsg = t || response.statusText;
    } catch {
      serverMsg = response.statusText;
    }
  }
  const requestId = response.headers.get("x-request-id");
  return new HttpError(response.status, url, humanize(response.status, serverMsg), requestId, details);
}

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  // seconds
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, seconds) * 1000;
  // HTTP-date
  const date = Date.parse(header);
  if (!Number.isNaN(date)) {
    const diff = date - Date.now();
    return diff > 0 ? diff : undefined;
  }
  return undefined;
}

function backoffWithJitter(attempt: number, base = 400, cap = 5000): number {
  const exp = Math.min(cap, base * Math.pow(2, attempt));
  const jitter = Math.random() * 200;
  return Math.round(exp + jitter);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
